import { createContext, useEffect, useState, useCallback } from "react";
import { fetchCurrentUser } from "../utils/SonicAPIV2";
import { useAuth } from "./auth.context";

const defaultCurrentUser = {
  outletName: "",
  customerCode: "",
  phoneNo: "",
  email: "",
  address: "",
  OTP: "",
  smsType: "",
  isloggedIn: false,
  customerDetails: null,
};

const defaultCustomerProfile = {
  customerName: "",
  customerCode: "",
  customerEmail: "",
  customerPhone: "",
  splGroupCode: "",
};

const defaultCustomerDetails = {
  landmark: "",
  bldgName: "",
  flatNumber: "",
  driverName: "",
  driverMobileNumber: "",
  splGroupCode: "",
  name: "",
  email: "",
  dob: "",
  countryID: 0,
  stateID: 0,
  cityID: 0,
  AreaId: 0,
  postCode: "",
  addresses: [
    {
      address: "",
      is_Default: true,
    },
  ],
  numbers: [
    {
      number: "",
      is_Default: false,
      countryId: 1,
    },
  ],
};

export const UserContext = createContext({
  currentUser: defaultCurrentUser,
  customerProfile: defaultCustomerProfile,
  customerDetails: defaultCustomerDetails,
  setCurrentUser: () => null,
  setCustomerProfile: () => null,
  setCustomerDetails: () => null,
  loadingDetails: false,
  refreshUserData: () => null,
});

export const UserProvider = ({ children }) => {
  const { authData, authLoading } = useAuth();
  const [currentUser, setCurrentUser] = useState(defaultCurrentUser);
  const [customerProfile, setCustomerProfile] = useState(
    defaultCustomerProfile
  );
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false); // ✅ Track if initial load is done

  // ✅ Extract the loading logic - NO dependencies that change
  const loadUserDetails = useCallback(
    async (force = false) => {
      // wait until auth check completes and we have authData
      if (authLoading) {
        // console.log("Auth still loading, skipping user details load");
        return;
      }

      if (!authData) {
        // console.log("No auth data, user not logged in");
        return;
      }

      // if already loaded and not forcing refresh, skip
      if (!force && hasLoaded) {
        // console.log("User details already loaded, skipping");
        return;
      }

      try {
        // console.log("Loading user details from server...");
        setLoadingDetails(true);
        const resp = await fetchCurrentUser();

        // console.log("Loaded customer details from Sonic:", resp);

        const sonic = resp;
        const customerProfileData = sonic.user.customerProfile;

        // Basic mapping: pick common fields safely
        const name = customerProfileData?.customerName || "";
        const email = customerProfileData?.email || "N/A";
        const phone = customerProfileData.phoneNo || "";
        const code = customerProfileData?.customerCode || "";
        const splGroupCode = customerProfileData?.splGroupCode || "";

        // update customer profile (small subset used in UI)
        setCustomerProfile({
          customerName: name || "",
          customerCode: code || "",
          customerEmail: email || "NA",
          customerPhone: phone || "",
          splGroupCode: splGroupCode || "",
        });

        // update customerDetails (store raw sonic payload for other uses)
        setCustomerDetails(sonic);

        // update currentUser summary used across app
        setCurrentUser({
          ...defaultCurrentUser,
          outletName: name,
          customerCode: code,
          phoneNo: phone,
          email,
          address: (sonic?.addresses && sonic.addresses[0]?.address) || "",
          isloggedIn: true,
          customerDetails: sonic,
        });

        setHasLoaded(true); // ✅ Mark as loaded
      } catch (err) {
        console.error("Failed to load customer details:", err);
      } finally {
        setLoadingDetails(false);
      }
    },
    [authData, authLoading, hasLoaded]
  ); // ✅ Only depend on stable values

  // ✅ Initial load - only run when auth state changes
  useEffect(() => {
    if (!authLoading && authData && !hasLoaded) {
      // console.log("Initial user details load triggered");
      loadUserDetails(false);
    }
  }, [authData, authLoading, hasLoaded, loadUserDetails]);

  // ✅ Expose a refresh function that can be called manually
  const refreshUserData = useCallback(async () => {
    // console.log("Manual refresh of user data requested");
    await loadUserDetails(true); // force refresh
  }, [loadUserDetails]);

  // ✅ Reset hasLoaded when user logs out
  useEffect(() => {
    if (!authData && hasLoaded) {
      // console.log(
      //   "User logged out, clearing user data and resetting load state"
      // );
      setHasLoaded(false);
      setCurrentUser(defaultCurrentUser);
      setCustomerProfile(defaultCustomerProfile);
      setCustomerDetails(null);
    }
  }, [authData, hasLoaded]);

  const value = {
    currentUser,
    setCurrentUser,
    customerProfile,
    setCustomerProfile,
    customerDetails,
    setCustomerDetails,
    loadingDetails,
    refreshUserData,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
